import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Check,
  ChevronDown,
  Edit,
  MoreHorizontal,
  Plus,
  Trash,
} from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import type { AgentTestHistoriesState } from "@/hooks/useAgentTestHistories"
import { useDeleteHistoryApi, useUpdateHistoryApi } from "@/hooks/api/histories"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import { hasPermission } from "@/lib/utils"
import { useStore } from "@/store"
import { History, Permission, Workspace } from "@/types"

interface HistoryRowMenuProps {
  history: History
  disabled: boolean
  canManage: boolean
  onMutationPendingChange: (pending: boolean) => void
  onRenamed: (history: History) => void
  onDeleted: (history: History) => void
}

const HistoryRowMenu = ({
  history,
  disabled,
  canManage,
  onMutationPendingChange,
  onRenamed,
  onDeleted,
}: HistoryRowMenuProps) => {
  const { t } = useTranslation()
  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [name, setName] = useState(history.name)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string>()
  const { fetchFn: updateHistory } = useUpdateHistoryApi(history.id)
  const { fetchFn: deleteHistory } = useDeleteHistoryApi(history.id)

  if (!canManage) return null

  const runMutation = async (mutation: () => Promise<void>) => {
    setPending(true)
    setError(undefined)
    onMutationPendingChange(true)
    try {
      await mutation()
    } catch (error) {
      setError(
        getUserFacingApiErrorMessage(
          error,
          t,
          t("workspace.agentPlayground.history.mutationFailed")
        )
      )
    } finally {
      setPending(false)
      onMutationPendingChange(false)
    }
  }

  const handleRename = () =>
    runMutation(async () => {
      const trimmedName = name.trim()
      if (!trimmedName) return
      const response = await updateHistory({ jsonBody: { name: trimmedName } })
      if (!response?.data) throw new Error("Missing history response")
      onRenamed({ ...history, name: trimmedName })
      setRenameOpen(false)
    })

  const handleDelete = () =>
    runMutation(async () => {
      const response = await deleteHistory()
      if (!response) throw new Error("Missing delete response")
      onDeleted(history)
      setDeleteOpen(false)
    })

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            disabled={disabled || pending}
            aria-label={t("workspace.agentPlayground.history.actions")}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onClick={(event) => event.stopPropagation()}
        >
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault()
              setName(history.name)
              setError(undefined)
              setRenameOpen(true)
            }}
          >
            <Edit className="mr-2 h-4 w-4" />
            {t("workspace.agentPlayground.history.rename")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={(event) => {
              event.preventDefault()
              setError(undefined)
              setDeleteOpen(true)
            }}
          >
            <Trash className="mr-2 h-4 w-4" />
            {t("workspace.agentPlayground.history.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={renameOpen}
        onOpenChange={(open) => !pending && setRenameOpen(open)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("workspace.agentPlayground.history.rename")}
            </DialogTitle>
            <DialogDescription>
              {t("workspace.agentPlayground.history.renameDescription")}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={pending}
            autoFocus
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => setRenameOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              disabled={pending || !name.trim()}
              onClick={() => void handleRename()}
            >
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteOpen}
        onOpenChange={(open) => !pending && setDeleteOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("workspace.agentPlayground.history.delete")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("workspace.agentPlayground.history.deleteDescription", {
                name: history.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={pending}
              onClick={(event) => {
                event.preventDefault()
                void handleDelete()
              }}
            >
              {t("workspace.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface AgentTestHistorySelectorProps {
  state: AgentTestHistoriesState
  workspace: Workspace
  disabled?: boolean
  mutationPending: boolean
  onMutationPendingChange: (pending: boolean) => void
  onSelect: (history: History) => Promise<boolean>
  onStartNew: () => Promise<boolean>
  onDeletedSelected: () => void
}

const AgentTestHistorySelector = ({
  state,
  workspace,
  disabled = false,
  mutationPending,
  onMutationPendingChange,
  onSelect,
  onStartNew,
  onDeletedSelected,
}: AgentTestHistorySelectorProps) => {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [switchError, setSwitchError] = useState<string>()
  const pendingSelectionRef = useRef<(() => Promise<boolean>) | undefined>(
    undefined
  )
  const { ensureFirstPage } = state
  const [userMe, currentOrganization] = useStore((store) => [
    store.userMe,
    store.currentOrganization,
  ])
  const editorPermissions = useMemo(
    () =>
      new Set([
        ...(workspace.myPermissions ?? new Set()),
        ...(currentOrganization?.myPermissions ?? new Set()),
      ]),
    [currentOrganization?.myPermissions, workspace.myPermissions]
  )

  useEffect(() => {
    if (open) void ensureFirstPage()
  }, [ensureFirstPage, open])

  const canManage = (history: History) =>
    history.creator?.id === userMe?.id ||
    hasPermission(editorPermissions, Permission.WsUpdateResources)

  const runPendingSelection = () => {
    const action = pendingSelectionRef.current
    if (!action) return
    pendingSelectionRef.current = undefined

    // Run after Radix finishes restoring focus for the closing menu. This keeps
    // the async load outside the menu's focus-management lifecycle.
    window.setTimeout(() => {
      const handleFailure = () => {
        setSwitchError(t("workspace.agentPlayground.history.switchFailed"))
        setOpen(true)
      }

      void action().then((committed) => {
        if (!committed) handleFailure()
      }, handleFailure)
    }, 0)
  }

  const select = (action: () => Promise<boolean>) => {
    setSwitchError(undefined)
    // Close the Radix focus scope before loading changes the disabled state of
    // the trigger. Keeping the menu open while the ThreadPanel enters its
    // loading state can make Radix repeatedly re-compose refs under React 19.
    pendingSelectionRef.current = action
    setOpen(false)
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-9 min-w-0 max-w-72 justify-between gap-2 font-normal"
          disabled={disabled || mutationPending}
        >
          <span className="truncate">
            {state.selectedHistory?.name ||
              t("workspace.agentPlayground.history.newTest")}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-80 p-1"
        onCloseAutoFocus={runPendingSelection}
      >
        <DropdownMenuItem onSelect={(event) => event.preventDefault()} asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2"
            disabled={disabled || mutationPending}
            onClick={() => void select(onStartNew)}
          >
            <Plus className="h-4 w-4" />
            {t("workspace.agentPlayground.history.startNew")}
          </button>
        </DropdownMenuItem>
        <DropdownMenuSeparator />

        <div className="max-h-80 overflow-y-auto">
          {state.isInitialLoading && (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              {t("workspace.agentPlayground.history.loading")}
            </p>
          )}
          {Boolean(state.initialError) && !state.hasLoaded && (
            <div className="space-y-2 px-2 py-4 text-center text-sm">
              <p className="text-destructive">
                {t("workspace.agentPlayground.history.loadFailed")}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void state.retryInitial()}
              >
                {t("workspace.agentPlayground.history.retry")}
              </Button>
            </div>
          )}
          {state.hasLoaded && state.rows.length === 0 && (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              {t("workspace.agentPlayground.history.empty")}
            </p>
          )}
          {state.rows.map((history) => {
            const selected = history.id === state.selectedHistory?.id
            return (
              <div
                key={history.id}
                className={cn(
                  "flex items-center rounded-sm pr-1 hover:bg-accent",
                  selected && "bg-accent"
                )}
              >
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-left"
                  disabled={disabled || mutationPending}
                  onClick={() => void select(() => onSelect(history))}
                >
                  <Check
                    className={cn("h-4 w-4 shrink-0", !selected && "invisible")}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {history.name}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {new Date(
                        history.updatedAt || history.createdAt
                      ).toLocaleString(i18n.resolvedLanguage || i18n.language)}
                    </span>
                  </span>
                </button>
                <HistoryRowMenu
                  history={history}
                  disabled={disabled || mutationPending}
                  canManage={canManage(history)}
                  onMutationPendingChange={onMutationPendingChange}
                  onRenamed={(renamed) => {
                    state.patchHistory(renamed.id, { name: renamed.name })
                    void state.refreshFirstPage()
                  }}
                  onDeleted={(deleted) => {
                    const wasSelected = deleted.id === state.selectedHistory?.id
                    state.removeHistory(deleted.id)
                    if (wasSelected) onDeletedSelected()
                    void state.refreshFirstPage()
                  }}
                />
              </div>
            )
          })}
        </div>

        {state.hasMore && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(event) => event.preventDefault()}
              asChild
            >
              <button
                type="button"
                className="w-full justify-center"
                disabled={state.isLoadingMore}
                onClick={() => void state.loadMore()}
              >
                {state.isLoadingMore
                  ? t("workspace.agentPlayground.history.loading")
                  : t("workspace.agentPlayground.history.loadMore")}
              </button>
            </DropdownMenuItem>
          </>
        )}
        {Boolean(state.loadMoreError) && (
          <div className="flex items-center justify-between gap-2 px-2 py-2 text-xs text-destructive">
            <span>{t("workspace.agentPlayground.history.loadMoreFailed")}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void state.retryLoadMore()}
            >
              {t("workspace.agentPlayground.history.retry")}
            </Button>
          </div>
        )}
        {switchError && (
          <div className="px-2 py-2 text-xs text-destructive">
            {switchError}
          </div>
        )}
        {Boolean(state.refreshError) && (
          <div className="flex items-center justify-between gap-2 px-2 py-2 text-xs text-destructive">
            <span>{t("workspace.agentPlayground.history.listStale")}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void state.refreshFirstPage()}
            >
              {t("workspace.agentPlayground.history.retry")}
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default AgentTestHistorySelector
